// Hook que invoca la RPC guardar_sesion_completada para cerrar una sesión
// del paciente. Patrón vanilla useState coherente con useEjerciciosDelDia.
//
// Llamada típica desde SesionCamara cuando mode pasa a 'finished':
//   void mutate({ sesion_id, total_reps, score_*, fps_avg, duration_sec, pose_analysis })

import { useState } from "react";
import { supabase } from "../client";
import type { BaselineSnapshot } from "../../pose/types";

/** Shape del jsonb que se persiste en sesiones.pose_analysis.
 *  version=1 para evaluators binarios (rot_externa_banda). */
export type PoseAnalysisV1 = {
  version: 1;
  detector: {
    schema_id: string;
    schema_version: string;
    evaluator_version: string;
  };
  captura: {
    user_agent: string;
  };
  ejecucion: {
    series: Array<{
      numero: number;
      reps_detectados: number;
      reps_validos: number;
      calidad_promedio: number;
      duracion_promedio_ms: number;
      prominencia_promedio_grados: number;
    }>;
  };
  calidad_global: {
    violaciones_frecuentes: Array<{
      tipo: string;
      frecuencia_pct: number;
    }>;
  };
};

/** Shape extendido para evaluators 3-level (squat_phases). Aditivo sobre
 *  V1: agrega `baseline` (snapshot capturado al inicio de la sesión) y
 *  `clasificacionModelo` para que el reader sepa cómo interpretar las
 *  métricas. Se discrimina por `version: 2`. C4 va a implementar el
 *  buildPoseAnalysis runtime que arma este shape. */
export type PoseAnalysisV2 = {
  version: 2;
  clasificacionModelo: "binario" | "3_niveles";
  detector: {
    schema_id: string;
    schema_version: string;
    evaluator_version: string;
  };
  captura: {
    user_agent: string;
  };
  /** Snapshot del baseline capturado pre-sesión. Solo presente cuando el
   *  schema lo requiere (sentadilla mini). Para schemas sin baseline,
   *  queda undefined. */
  baseline?: BaselineSnapshot;
  ejecucion: {
    series: Array<{
      numero: number;
      reps_detectados: number;
      reps_validos: number;
      /** Conteo por nivel — solo poblado cuando clasificacionModelo === '3_niveles'.
       *  Para binario, la información vive en reps_validos. */
      reps_por_nivel?: {
        ideal: number;
        acceptable: number;
        invalid: number;
      };
      calidad_promedio: number;
      duracion_promedio_ms: number;
      prominencia_promedio_grados: number;
    }>;
  };
  calidad_global: {
    violaciones_frecuentes: Array<{
      tipo: string;
      frecuencia_pct: number;
    }>;
  };
};

/** Union persistida. Los readers branchean por `version`. */
export type PoseAnalysis = PoseAnalysisV1 | PoseAnalysisV2;

export interface SesionCompletadaInput {
  sesion_id: string;
  total_reps: number;
  /** null cuando la sesión no tuvo reps válidas. */
  score_promedio: number | null;
  score_minimo: number | null;
  score_maximo: number | null;
  fps_avg: number;
  duration_sec: number;
  /** null cuando la sesión es video_only (mov_cuello) y no hay detección
   *  de pose. Para flow cámara, siempre poblado. */
  pose_analysis: PoseAnalysis | null;
  /** Nota opcional escrita por el paciente al cerrar sesión (textarea
   *  "¿Cómo te sentiste?"). Hoy solo usado por VideoOnlyView, pero queda
   *  en la interfaz para que cuando el flow de cámara también lo soporte
   *  el hook ya esté preparado. */
  notas_paciente?: string | null;
}

export interface UseGuardarSesionReturn {
  mutate: (input: SesionCompletadaInput) => Promise<void>;
  loading: boolean;
  error: Error | null;
  success: boolean;
}

export function useGuardarSesionCompletada(): UseGuardarSesionReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  async function mutate(input: SesionCompletadaInput) {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const { error: rpcError } = await supabase.rpc(
        "guardar_sesion_completada",
        {
          p_sesion_id: input.sesion_id,
          p_total_reps: input.total_reps,
          p_score_promedio: input.score_promedio,
          p_score_minimo: input.score_minimo,
          p_score_maximo: input.score_maximo,
          p_fps_avg: input.fps_avg,
          p_duration_sec: input.duration_sec,
          p_pose_analysis: input.pose_analysis,
          p_notas_paciente: input.notas_paciente ?? null,
        },
      );
      if (rpcError) throw rpcError;
      setSuccess(true);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      console.error("[useGuardarSesionCompletada] failed:", err);
    } finally {
      setLoading(false);
    }
  }

  return { mutate, loading, error, success };
}
